import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { SiteFooter } from "@/components/SiteFooter";
import { Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    if (busy) return;
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { setErr("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (e2) {
      setErr(apiError(e2));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md pc-reveal">
        <Link to="/login" className="flex items-center gap-3 mb-8" aria-label="PropertyConnect home">
          <BrandLogo className="h-[88px]" />
        </Link>

        {!token ? (
          <div data-testid="reset-invalid">
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Invalid reset link</h1>
            <p className="text-slate-500 mt-2">This password reset link is missing or malformed. Please request a new one.</p>
            <Link to="/forgot-password" className="mt-8 inline-flex items-center gap-2 text-accent font-semibold hover:underline" data-testid="request-new-link">
              <ArrowLeft className="h-4 w-4" /> Request a new link
            </Link>
          </div>
        ) : done ? (
          <div data-testid="reset-success">
            <div className="h-12 w-12 rounded-2xl border border-accent/40 flex items-center justify-center mb-5">
              <ShieldCheck className="h-6 w-6 text-accent" />
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Password updated</h1>
            <p className="text-slate-500 mt-2">Your password has been changed. Redirecting you to sign in…</p>
            <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-accent font-semibold hover:underline" data-testid="reset-to-login">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Set a new password</h1>
            <p className="text-slate-500 mt-2">Choose a new password for your account. Reset links expire after 1 hour and can be used once.</p>
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
              <div>
                <Label htmlFor="reset-pw">New password</Label>
                <Input id="reset-pw" data-testid="reset-password-input" type="password" autoComplete="new-password"
                  value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" className="mt-1.5 h-11" required />
              </div>
              <div>
                <Label htmlFor="reset-confirm">Confirm new password</Label>
                <Input id="reset-confirm" data-testid="reset-confirm-input" type="password" autoComplete="new-password"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter new password" className="mt-1.5 h-11" required />
              </div>
              {err && <p data-testid="reset-error" role="alert" className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{err}</p>}
              <Button data-testid="reset-submit" type="submit" disabled={busy} className="w-full h-11 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-xl">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update password"}
              </Button>
            </form>
            <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors" data-testid="reset-back-to-login">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </>
        )}
        <SiteFooter variant="minimal" />
      </div>
    </div>
  );
}
